import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Mail, ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import { api } from '../lib/api';
import { FormError, FormBanner } from '../components/FormError';
import useFormValidation from '../hooks/useFormValidation';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const v = useFormValidation();

  const submit = async (e) => {
    e.preventDefault();
    v.reset();
    const ok = v.validate({
      email: !email ? 'Email es obligatorio.'
        : !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) ? 'Email no tiene un formato válido.'
        : null,
    });
    if (!ok) return;
    setLoading(true);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      v.applyError(err, 'No pudimos procesar tu solicitud. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-habita-cream flex flex-col p-6 lg:p-12">
      <SEO title="Recuperar contraseña" noindex />
      <Link to="/" className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-md bg-habita flex items-center justify-center"><HomeIcon className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
        <span className="font-serif font-bold text-2xl text-habita">habita<span className="text-amber-500">.</span><span className="text-gray-800">co</span></span>
      </Link>

      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 lg:p-10 border border-gray-100 shadow-sm">
          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-habita-mint mx-auto flex items-center justify-center"><Check className="w-8 h-8 text-habita" strokeWidth={3} /></div>
              <h1 className="font-serif text-3xl font-bold text-habita-dark mt-5">Revisa tu correo</h1>
              <p className="text-gray-600 mt-3">Si existe una cuenta con ese email, te enviamos un enlace para restablecer tu contraseña.</p>
              <p className="text-xs text-gray-500 mt-2">Revisa también la carpeta de spam.</p>
              <Link to="/login" className="btn-habita inline-flex items-center gap-2 mt-6 px-6 h-11 rounded-full font-semibold"><ArrowLeft className="w-4 h-4" /> Volver a iniciar sesión</Link>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-3xl lg:text-4xl font-bold text-habita-dark">¿Olvidaste tu contraseña?</h1>
              <p className="text-gray-600 mt-3">Ingresa tu email y te enviaremos un enlace para crear una nueva.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
                <FormBanner msg={v.banner} testid="forgot-error-banner" />
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={e => { setEmail(e.target.value); v.clearField('email'); }}
                      className={`h-12 pl-10 ${v.errorClass('email')}`}
                      placeholder="tu@correo.com"
                      data-testid="forgot-email-input"
                      {...v.field('email')}
                    />
                  </div>
                  <FormError msg={v.fieldErrors.email} testid="forgot-error-email" />
                </div>
                <Button type="submit" disabled={loading} className="btn-habita w-full h-12 rounded-full font-semibold disabled:opacity-60" data-testid="forgot-submit-btn">
                  {loading ? 'Enviando...' : 'Enviar enlace'}
                </Button>
              </form>
              <Link to="/login" className="flex items-center justify-center gap-1 mt-6 text-sm text-gray-600 hover:text-habita"><ArrowLeft className="w-4 h-4" /> Volver a iniciar sesión</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
